import { LegalFormValue } from '../config/legalForms';

// Типы для API справочников
export interface City {
  id: number;
  name: string;
  region: string;
  country: string;
  /** Центр города. `CityResource` приводит к числу; `null` — не заполнен в админке. */
  latitude?: number | null;
  longitude?: number | null;
  created_at: string;
  updated_at: string;
}

// Категория = направление бизнеса: ресторан, кафе, барбершоп, автомойка.
// Одна из категорий заведения помечена основной (is_primary) — по ней заведение
// представлено в каталоге.
export interface Category {
  id: number;
  title: string;
  description?: string | null;
  image?: string | null;
  /** Иконка категории (одноцветный SVG или PNG). Нет поля или `null` — иконки нет. */
  icon_url?: string | null;
  position: number;
  is_primary?: boolean;
  created_at?: string;
  updated_at?: string;
}

// Подкатегория = специализация внутри супер-категории: кухни, виды услуг.
// Список ведёт админ, партнёр только выбирает — иначе фильтр в приложении
// расползётся на «паназиатская / Пан-Азиатская / PanAsian».
export interface Subcategory {
  id: number;
  title: string;
  description?: string | null;
  position?: number;
  category_group_id: number;
}

// Супер-категория: Поесть, Развлечения, Красота… Держит и категории, и
// подкатегории. Одна категория может входить в несколько групп — «Бары» есть
// и в «Поесть», и в «Развлечениях».
export interface CategoryGroup {
  id: number;
  title: string;
  description?: string | null;
  image?: string | null;
  /** Иконка супер-категории (одноцветный SVG или PNG), красится в `color`. Нет поля или `null` — иконки нет. */
  icon_url?: string | null;
  /** Цвет супер-категории `#RRGGBB`. Нет поля или `null` — цвет не задан. */
  color?: string | null;
  position: number;
  categories: Category[];
  subcategories: Subcategory[];
}

// Тариф из справочника. Неизменяем: заводится один раз и меняет состояние
// ровно один раз, поэтому во фронте его условия не дублируем — только читаем.
export interface Tariff {
  id: number;
  name: string;
  duration_days: number;
  price: string;
  is_default: boolean;
  is_trial: boolean;
}

// Типы для API партнера
export interface User {
  id: number;
  name: string;
  phone: string;
}

export interface PartnerImage {
  id: number;
  type: 'main' | 'cover' | 'partner';
  url: string;
  created_at: string;
  updated_at: string;
}

export interface WorkingHour {
  id: number;
  day_of_week: number; // 0=Воскресенье, 1=Понедельник ... 6=Суббота
  day_label: string;
  open_time: string | null;
  close_time: string | null;
  is_day_off: boolean;
  closes_next_day: boolean;
  break_start: string | null;
  break_end: string | null;
  /** Круглосуточный день (ADR-027): сервер считает по временам. У старого
   *  сервера поля нет — тогда см. `isRoundTheClock` в utils/workingHours. */
  is_round_the_clock?: boolean;
}

export interface WorkingHourCreateRequest {
  day_of_week: number;
  open_time?: string | null;
  close_time?: string | null;
  is_day_off?: boolean;
  closes_next_day?: boolean;
  break_start?: string | null;
  break_end?: string | null;
  /** true — сервер сам приводит день к каноничному 00:00 → 00:00⁺¹ без перерыва (ADR-027). */
  is_round_the_clock?: boolean;
}

export interface WorkingHourUpdateRequest {
  open_time?: string | null;
  close_time?: string | null;
  is_day_off?: boolean;
  closes_next_day?: boolean;
  break_start?: string | null;
  break_end?: string | null;
  /** true — сервер сам приводит день к каноничному 00:00 → 00:00⁺¹ без перерыва (ADR-027). */
  is_round_the_clock?: boolean;
}

export interface ActivationChecks {
  name: boolean;
  address: boolean;
  city: boolean;
  /** Точка на карте (с 01.10.2026, ADR-014): без неё запуск недоступен. */
  location: boolean;
  categories: boolean;
  menu_items: boolean;
}

/** Черновик точки по адресу — `GET /api/partner/geocode` (ADR-014). */
/** Видеоинструкция «как пользоваться» — справочник `GET /references/video-guides`. */
export interface VideoGuide {
  id: number;
  title: string;
  url: string;
  /** Кадр YouTube; у роликов с других хостингов — null. */
  thumbnail: string | null;
  audience: 'client' | 'partner';
}

export interface GeocodedAddress {
  latitude: number;
  longitude: number;
  label: string;
  /** house — найден дом; street — только улица; area — район или город. */
  precision: 'house' | 'street' | 'area';
}

export interface ActivationStatus {
  is_activated: boolean;
  requirements_met: boolean;
  checks: ActivationChecks;
  expires_at?: string | null;
  activated_at?: string | null;
}

// Активация стала осознанным действием партнёра: раньше её дёргал observer
// после каждой правки профиля, и пробный период начинал течь, пока заведение
// ещё настраивали. Теперь — кнопка.
export interface ActivateResponse {
  message: string;
  activated_now: boolean;
  data: ActivationStatus & {
    trial_granted?: boolean;
    is_on_trial?: boolean;
  };
}

export interface Subscription {
  is_active: boolean;
  is_on_trial: boolean;
  is_activated?: boolean;
  can_edit?: boolean;
  expires_at: string | null;
  activated_at?: string | null;
  trial_started_at: string | null;
  days_left: number | null;
  activation?: ActivationStatus;
}

export interface Tag {
  id: number;
  slug: string;
  name: string;
  is_curated?: boolean;
  usage_count?: number;
}

export interface Partner {
  id: number;
  name: string;
  address?: string;
  description?: string;
  // Колонки decimal без каста — API отдаёт строку «51.1694000». Читать через
  // parseCoordinate (utils/coordinates), а не сравнивать typeof с number.
  longitude?: number | string | null;
  latitude?: number | string | null;
  city?: City;
  categories?: Category[];
  primary_category?: Pick<Category, 'id' | 'title'> | null;
  subcategories?: Subcategory[];
  tags?: Tag[];
  main_image?: PartnerImage;
  /** Обложка заведения. `null` — не загружена, шапку рисуем главным фото. */
  cover_image?: PartnerImage | null;
  additional_images?: PartnerImage[];
  working_hours?: WorkingHour[];
  telegram?: string;
  whatsapp?: string;
  instagram?: string;
  youtube?: string;
  facebook?: string;
  linkedin?: string;
  website?: string;
  average_rating?: number;
  expires_at?: string;
  subscription?: Subscription;
  /** Заполненность карточки — считает сервер (БП 5.1). */
  completeness?: ProfileCompleteness;
  /** Статус публикации — отдельно от активации (БП 5.4). */
  publication?: PublicationState;
  /**
   * Юридические реквизиты и акцепт оферты. `null`, пока партнёр ничего не
   * заполнял: анкета регистрации реквизиты не спрашивает, а в настройках они
   * необязательные. `PUT /partner/profile` их не принимает — у них свой
   * `PUT /partner/legal`.
   */
  legal?: LegalDetail | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * `GET /api/partner/profile` → `data.publication`.
 *
 * Активация — про оплату и готовность, публикация — про видимость в
 * приложении и решение модератора. Плашка на обложке показывает второе.
 */
export interface PublicationState {
  status: 'draft' | 'moderation' | 'published' | 'rejected' | 'paused';
  label: string;
  color: string;
  /** Причина отказа — партнёр должен понять, что исправить. */
  reason: string | null;
  is_visible: boolean;
  moderated_at: string | null;
}

/** `GET /api/partner/profile` → `data.completeness`. */
export interface ProfileCompleteness {
  percent: number;
  done_count: number;
  total: number;
  items: ProfileCompletenessItem[];
}

export interface ProfileCompletenessItem {
  key: string;
  label: string;
  hint: string;
  done: boolean;
  required: boolean;
}

export interface PartnerProfileResponse {
  data: Partner;
}

export interface AuthResponse {
  message: string;
  user: User;
  partner: Partner;
  token: string;
}

/**
 * `POST /partner/registration/verify` — шаг 2 регистрации.
 *
 * Токен уже действителен, но заведения ещё нет: до отправки анкеты все
 * маршруты кабинета отвечают `409 registration_incomplete`.
 */
export interface RegistrationVerifyResponse {
  message: string;
  registration_required: boolean;
  user: User;
  token: string;
}

/**
 * Анкета регистрации: заведение и акцепт оферты.
 *
 * Реквизитов (`legal`) в запросе нет: анкета их больше не спрашивает.
 * `venue.address` — фактический адрес заведения, по нему приложение строит
 * маршрут гостю.
 */
export interface RegistrationRequest {
  venue: {
    name: string;
    city_id: number;
    address: string;
    category_id: number;
    /** Точка на карте — только парой и только если партнёр её поставил. */
    latitude?: number;
    longitude?: number;
  };
  offer: {
    version: string;
    accepted: true;
  };
}

export interface RegistrationResponse {
  message: string;
  partner: Partner;
}

/**
 * `GET /partner/profile` → `data.legal`, ответ `PUT /partner/legal` → `data`.
 *
 * Реквизиты необязательные и заполняются любым подмножеством, поэтому `null`
 * бывает на двух уровнях: весь объект — ничего не заполняли, отдельное поле —
 * его не заполнили. Клиент обязан переживать оба.
 *
 * IP акцепта наружу не отдаётся даже владельцу — он нужен для разбора спора,
 * а не для показа в кабинете.
 */
export interface LegalDetail {
  form: LegalFormValue | null;
  form_label: string | null;
  /** Подпись поля приходит с сервера: «БИН», «ИИН» или «БИН/ИИН», пока форма не выбрана. */
  identifier_label: string;
  name: string | null;
  registration_number: string | null;
  /** Юридический адрес — реквизит договора, не адрес заведения. */
  address: string | null;
  /** `null` у партнёров, которые оферту в кабинете не принимали. */
  offer: {
    version: string;
    accepted_at: string;
  } | null;
}

/**
 * `PUT /partner/legal` — полная замена: не пришедшее или пустое поле
 * очищается. Поэтому клиент шлёт все четыре ключа, пустые — `null`.
 */
export interface UpdateLegalRequest {
  form: LegalFormValue | null;
  name: string | null;
  registration_number: string | null;
  address: string | null;
}

export interface UpdateLegalResponse {
  message: string;
  data: LegalDetail;
}

export interface PartnerUpdateRequest {
  name?: string;
  address?: string;
  description?: string;
  longitude?: number;
  latitude?: number;
  city_id?: number;
  primary_category_id?: number | null;
  categories?: number[];
  subcategories?: number[];
  whatsapp?: string;
  instagram?: string;
  youtube?: string;
  facebook?: string;
  linkedin?: string;
  website?: string;
}

export interface PartnerUpdateResponse {
  message: string;
  partner: Partner;
}

// Типы для форм
export interface PhoneFormData {
  phone: string;
}

export interface VerifyFormData {
  phone: string;
  code: string;
}

export interface PartnerFormData {
  name: string;
  address: string;
  description?: string;
  longitude?: number;
  latitude?: number;
  city_id: number;
  // Основная категория заведения. null — партнёр её ещё не выбрал.
  primary_category_id: number | null;
  // Дополнительные категории, без основной.
  categories: number[];
  subcategories: number[];
  main_image?: File | null; // Основное изображение (аватар заведения)
  cover_image?: File | null; // Обложка — широкий кадр шапки (БП 5.2)
  additional_images?: File[]; // Дополнительные изображения
  whatsapp?: string;
  instagram?: string;
  youtube?: string;
  facebook?: string;
  linkedin?: string;
  website?: string;
}

// Типы для API категорий меню
/** Звено пути раздела от корня: «Бар / Безалкогольные / Чаи». */
export interface MenuCategoryPathNode {
  id: number;
  name: string;
}

/**
 * Раздел каталога. Разделы образуют дерево, товары лежат только в конечных:
 * у раздела с подразделами позиций нет и быть не может.
 *
 * Список приходит плоским, но в порядке обхода дерева — родитель, следом его
 * поддерево. Вложенность собирается по `parent_id`, отступ рисуется по
 * `depth`.
 */
export interface MenuCategory {
  id: number;
  name: string;
  partner_id: number;
  /** Родительский раздел; `null` у разделов верхнего уровня. */
  parent_id: number | null;
  /** Уровень вложенности: 0 у корневых. */
  depth: number;
  /** Ручной порядок среди соседей. */
  position: number;
  /** Сколько прямых подразделов. */
  children_count?: number;
  /** Конечный раздел — только в такой можно класть товары. */
  is_leaf?: boolean;
  /** Путь сверху вниз, включая сам раздел. */
  path?: MenuCategoryPathNode[];
  /** Приходит только там, где бэк подгрузил позиции: в списке категорий. */
  menu_items_count?: number;
  created_at: string;
  updated_at: string;
}

/**
 * Состояние позиции на витрине.
 *
 * `paused` и `hidden` для пользователя ведут себя одинаково — обе не видны;
 * различаются намерением партнёра, вернёт он позицию или снял насовсем.
 */
export type MenuItemStatus = 'active' | 'paused' | 'hidden';

export const MENU_ITEM_STATUS_LABELS: Record<MenuItemStatus, string> = {
  active: 'В продаже',
  paused: 'Временно нет',
  hidden: 'Скрыта',
};

export interface MenuItemImage {
  id: number;
  url: string;
}

export interface MenuItem {
  id: number;
  name: string;
  menu_category_id: number;
  price: number;
  /** Состав, вес, порция — вторая строка под названием. */
  description?: string | null;
  /**
   * Лучшая доступная скидка в процентах. Считает бэк: расписание заведения и
   * его часовой пояс клиенту неизвестны. Приходит не со всех эндпоинтов.
   * У скидки в тенге — лишь эквивалент для этой позиции (ADR-026).
   */
  best_discount_percent?: number;
  /**
   * Как задана лучшая скидка: от него зависит подпись — «−500 ₸», а не
   * «−8 %». null — скидки нет; поля нет у ответов до ADR-026.
   */
  best_discount_value_type?: DiscountValueType | null;
  /** Цена с этой скидкой, в целых тенге. Без скидок равна `price`. */
  price_with_discount?: number;
  /** Эта скидка идёт прямо сейчас, а не «сегодня вечером». */
  discount_active_now?: boolean;
  /** Состояние на витрине. Пользователю видны только `active`. */
  status?: MenuItemStatus;
  /** Ручной порядок внутри категории; 0 — по алфавиту. */
  position?: number;
  images: MenuItemImage[];
  created_at: string;
  updated_at: string;
}

export interface MenuCategoriesResponse {
  data: MenuCategory[];
}

export interface MenuItemsResponse {
  data: MenuItem[];
}

/** Фильтры плоского списка позиций: `GET /partner/menu-items`. */
export interface MenuItemsQuery {
  categoryId?: number;
  search?: string;
  page?: number;
  perPage?: number;
}

/** Страница плоского списка позиций вместе со счётчиком всей выборки. */
export interface MenuItemsPage {
  items: MenuItem[];
  total: number;
  page: number;
  perPage: number;
}

export interface MenuItemResponse {
  data: MenuItem;
}

// Типы для запросов
export interface CreateMenuCategoryRequest {
  name: string;
  /** Внутри какого раздела заводим; отсутствует — верхний уровень. */
  parent_id?: number | null;
}

/**
 * Переименование и перенос — один запрос. Поле, которого нет, не трогается:
 * переименование не должно распрямлять дерево.
 */
export interface UpdateMenuCategoryRequest {
  name?: string;
  parent_id?: number | null;
  position?: number;
}

export interface CreateMenuItemRequest {
  name: string;
  menu_category_id: number;
  price: number;
  description?: string | null;
  status?: MenuItemStatus;
  position?: number;
  images?: File[];
}

export interface UpdateMenuItemRequest {
  name?: string;
  menu_category_id?: number;
  price?: number;
  description?: string | null;
  status?: MenuItemStatus;
  position?: number;
  images?: File[];
  images_to_delete?: number[];
}

// Типы для API скидок (ADR-026)

/** Вид скидки: на группу позиций либо комбо — набор позиций за общую цену. */
export type DiscountKind = 'group' | 'combo';

/** Как задан размер групповой скидки: процент или тенге с каждой позиции. */
export type DiscountValueType = 'percent' | 'amount';

/** Позиция в составе комбо — как её отдаёт бэк: с текущей ценой меню. */
export interface DiscountComboItem {
  menu_item_id: number;
  name: string;
  price: number;
  quantity: number;
}

export interface Discount {
  id: number;
  /** Название акции — «Кальян + чай до 18:00». Необязательное. */
  name?: string | null;
  /**
   * Окно внутри суток, «HH:MM:SS». null бывает у скидок из админки: такая
   * скидка не идёт никогда, и форма не должна на ней падать.
   */
  time_start: string | null;
  time_end: string | null;
  /** 0 (вс) … 6 (сб). null и пустой массив — дни не заданы, скидка не идёт. */
  weekdays?: number[] | null;
  weekday?: number; // Для обратной совместимости
  /** Первый день срока, Y-M-D. null — акция уже идёт. */
  starts_at?: string | null;
  /** Последний день срока включительно, Y-M-D. null — бессрочная. */
  ends_at?: string | null;
  /** Ручной выключатель. Выключенная акция не видна в приложении. */
  is_active?: boolean;
  /**
   * Процент 1–100. У тенге и комбо — лишь эквивалент для сортировки и старых
   * сборок: размером скидки его не показывать.
   */
  discount: number;
  /** Цель групповой скидки. У комбо смысла не несёт. */
  discount_type?: 'product' | 'category';
  /** Нет у ответов до ADR-026 — это `group`. */
  kind?: DiscountKind;
  /** Нет у ответов до ADR-026 — это `percent`. */
  value_type?: DiscountValueType | null;
  /** ₸ с каждой позиции — у `value_type = amount`. */
  amount?: number | null;
  /** ₸ за весь набор — у комбо. */
  bundle_price?: number | null;
  /** Σ цена × количество по текущему меню — у комбо. */
  regular_price?: number | null;
  /** Состав комбо. */
  combo_items?: DiscountComboItem[];
  menu_items?: MenuItem[];
  menu_item_ids?: number[];
  menu_category?: MenuCategory;
  menu_category_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface DiscountsResponse {
  data: Discount[];
}

export interface DiscountResponse {
  discount: Discount;
}

/** Позиция в составе комбо — в запросе. */
export interface DiscountComboItemRequest {
  menu_item_id: number;
  /** 1–10. */
  quantity: number;
}

/** Общее у всех видов: имя, окно, дни и срок. */
interface DiscountScheduleRequest {
  name?: string | null;
  /** «HH:MM:SS». */
  time_start: string;
  time_end: string;
  weekdays: number[];
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
}

/** Размер групповой скидки — ровно одно из двух полей. */
type GroupDiscountValueRequest =
  | { value_type: 'percent'; discount: number }
  | { value_type: 'amount'; amount: number };

/** Цель групповой скидки — ровно одно из двух полей. */
type GroupDiscountTargetRequest = { menu_category_id: number } | { menu_item_ids: number[] };

export type GroupDiscountRequest = DiscountScheduleRequest & {
  kind: 'group';
} & GroupDiscountValueRequest &
  GroupDiscountTargetRequest;

export interface ComboDiscountRequest extends DiscountScheduleRequest {
  kind: 'combo';
  /** ₸ за весь набор, меньше Σ цена × количество. */
  bundle_price: number;
  combo_items: DiscountComboItemRequest[];
}

/**
 * Тело `POST /partner/discounts` и `PUT /partner/discounts/{id}`. Форма шлёт
 * его целиком в обоих случаях, поэтому тип у создания и правки один.
 */
export type DiscountRequest = GroupDiscountRequest | ComboDiscountRequest;
export type CreateDiscountRequest = DiscountRequest;
export type UpdateDiscountRequest = DiscountRequest;

/** Ответ создания и правки. Ошибки 422 — объект Laravel, см. `apiFieldErrors`. */
export interface DiscountMessageResponse {
  message: string;
  discount?: Discount;
}

// Типы для API историй (Stories)
export interface Story {
  id: number;
  partner_id: number;
  file_path: string;
  file_type: 'image' | 'video';
  title?: string;
  description?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface StoriesResponse {
  data: Story[];
}

export interface CreateStoryRequest {
  file: File;
  title?: string;
  description?: string;
}

export interface CreateStoryResponse {
  message: string;
  data: Story;
}


export interface DeleteStoryResponse {
  message: string;
}

// Типы для Дашборда
export interface DashboardStats {
  menu_categories_count: number;
  discounts_count: number;
  average_rating: number;
  reviews_count: number;
}

export interface DashboardReviewUser {
  id: number;
  name: string;
  avatar: string | null;
}

export interface DashboardReviewImage {
  id: number;
  url: string;
}

export interface DashboardReview {
  id: number;
  user: DashboardReviewUser;
  rating: number;
  text: string;
  reply_text?: string | null;
  replied_at?: string | null;
  created_at: string;
  images?: DashboardReviewImage[];
}

export interface DashboardResponse {
  stats: DashboardStats;
  recent_reviews: DashboardReview[];
  subscription?: Subscription;
}

// Аналитика трафика: GET /partner/analytics (см. partner-dashboard-api.md)
export type AnalyticsPeriod = '7d' | '30d' | '90d';

export interface AnalyticsSeriesPoint {
  date: string;
  value: number;
}

export interface AnalyticsMetric {
  value: number;
  /** null — в прошлом периоде не было событий, сравнивать не с чем. */
  delta_percent: number | null;
  /** По точке на календарный день окна, дни без событий — нулями. */
  series: AnalyticsSeriesPoint[];
}

export interface AnalyticsResponse {
  period: AnalyticsPeriod;
  metrics: {
    partner_views: AnalyticsMetric;
    catalog_opens: AnalyticsMetric;
    discount_opens: AnalyticsMetric;
    contact_clicks: AnalyticsMetric;
    /** Оффлайн-канал: сканы QR-таблички (`?src=qr`), отдельно от catalog_opens. */
    qr_scans: AnalyticsMetric;
    /** Оффлайн-канал: касания NFC-метки (`?src=nfc`). */
    nfc_taps: AnalyticsMetric;
  };
}

// === Рекламные кампании (платные места) — M14 ===

/** Поверхность показа: верх поиска, главная или обе сразу. */
export type AdPlacement = 'search' | 'home' | 'both';

/**
 * Жизненный цикл кампании. Партнёру доступны только pause/resume и архив:
 * `pending` активирует админ после согласования оплаты, вес ротации тоже
 * назначает админ.
 */
export type AdCampaignStatus = 'pending' | 'active' | 'paused' | 'archived';

export const AD_PLACEMENT_LABELS: Record<AdPlacement, string> = {
  search: 'В поиске',
  home: 'На главной',
  both: 'Поиск + главная',
};

export const AD_CAMPAIGN_STATUS_LABELS: Record<AdCampaignStatus, string> = {
  pending: 'На модерации',
  active: 'Активна',
  paused: 'На паузе',
  archived: 'В архиве',
};

/**
 * `GET /partner/ad-campaigns` → элемент `data`.
 *
 * Схема расписания повторяет скидки: weekdays 0 (вс) … 6 (сб), окно умеет
 * переходить полночь. NULL-поля означают «без ограничения»: weekdays — все
 * дни, time_start/time_end — весь день, ends_at — бессрочно.
 */
export interface AdCampaign {
  id: number;
  placement: AdPlacement;
  status: AdCampaignStatus;
  /** Первый день показов, Y-M-D. */
  starts_at: string | null;
  /** Последний день включительно, Y-M-D. null — бессрочно. */
  ends_at: string | null;
  /** Дни недели 0 (вс) … 6 (сб). null — все дни. */
  weekdays: number[] | null;
  /** Окно внутри суток, H:i:s. null — весь день. Поля парные. */
  time_start: string | null;
  time_end: string | null;
  /** Вес в ротации. Показывается для прозрачности, правит его только админ. */
  weight: number;
  created_at: string;
  updated_at: string;
}

export interface CreateAdCampaignRequest {
  placement: AdPlacement;
  starts_at: string;
  ends_at?: string | null;
  weekdays?: number[] | null;
  time_start?: string | null;
  time_end?: string | null;
}

export interface UpdateAdCampaignRequest {
  placement?: AdPlacement;
  starts_at?: string;
  ends_at?: string | null;
  weekdays?: number[] | null;
  time_start?: string | null;
  time_end?: string | null;
}

/** Ответ create/update/pause/resume: сообщение + свежая кампания. */
export interface AdCampaignMutationResponse {
  message: string;
  ad_campaign: AdCampaign;
}

// === QR-таблички и NFC-метки со столиков ===

/** Куда ведёт код: меню заведения или его карточка (`App\Enums\QrTarget`). */
export type QrTarget = 'menu' | 'partner';

/** Носитель: печатный код или метка (`App\Enums\QrSource`). */
export type QrSource = 'qr' | 'nfc';

/**
 * Готовая ссылка для печати.
 *
 * Формат адреса собирает бэк и только он: табличку печатают один раз, и
 * разъехавшийся формат означает пачку бумаги в мусор. Кабинет рисует по
 * этой строке картинку кода и больше ничего с ней не делает.
 */
export interface QrLink {
  /** Номер столика или null для носителя без привязки (вход, витрина). */
  table: number | null;
  target: QrTarget;
  source: QrSource;
  url: string;
  /** Человеческая подпись под кодом — её же печатают на табличке. */
  label: string;
}

// === Жалобы гостей на отзывы (read-only витрина кабинета) ===

/** Причина жалобы — закрытый список бэка (`App\Enums\ReviewReportReason`). */
export type ReviewReportReason = 'spam' | 'offensive' | 'fake' | 'other';

/**
 * Исход жалобы (`App\Enums\ReviewReportStatus`): `resolved` — жалоба
 * подтверждена и отзыв снят, `dismissed` — отклонена, отзыв оставлен.
 * Бэк шлёт именно `dismissed`, не `rejected`.
 */
export type ReviewReportStatus = 'pending' | 'resolved' | 'dismissed';

/** Подписи причин — слово в слово как в мобильном кабинете (ReportReason). */
export const REVIEW_REPORT_REASON_LABELS: Record<ReviewReportReason, string> = {
  spam: 'Спам',
  offensive: 'Оскорбления и ненависть',
  fake: 'Ложная информация',
  other: 'Другое',
};

/** Подписи исходов — слово в слово как в мобильном кабинете (ReportStatus). */
export const REVIEW_REPORT_STATUS_LABELS: Record<ReviewReportStatus, string> = {
  pending: 'На рассмотрении',
  resolved: 'Отзыв удалён',
  dismissed: 'Жалоба отклонена',
};

/**
 * Жалоба глазами партнёра: причина, комментарий и исход. Личность жалобщика
 * бэк намеренно не отдаёт — ни `user`, ни `user_id` в ответе нет и не будет,
 * ровно пять полей.
 */
export interface ReviewReport {
  id: number;
  reason: ReviewReportReason;
  comment: string | null;
  status: ReviewReportStatus;
  /** Y-m-d H:i:s. */
  created_at: string;
}

/** Автор отзыва. id null — аккаунт удалён, имя тогда заглушка с бэка. */
export interface ReportedReviewUser {
  id: number | null;
  name: string;
  avatar: string | null;
  /** Телефон в маскированном виде («+7 707 *** 45 67») либо null. */
  phone: string | null;
}

/**
 * `GET /partner/review-reports` → элемент `data`: публичная форма отзыва
 * плюс `hidden_at` и `reports`. Отзыв со свежей жалобой сверху, жалобы
 * внутри `reports` — тоже новые первыми.
 */
export interface ReportedReview {
  id: number;
  rating: number;
  text: string | null;
  reply_text: string | null;
  replied_at: string | null;
  created_at: string;
  updated_at: string;
  images: DashboardReviewImage[];
  user: ReportedReviewUser;
  partner_id: number;
  /**
   * Когда отзыв снят с публикации; null — виден. Отдельно от статуса жалобы:
   * модератор может вернуть отзыв, жалобы при этом остаются `resolved`.
   */
  hidden_at: string | null;
  reports: ReviewReport[];
}

// Типы для API баннеров
export interface Banner {
  id: string;
  partner_id: string;
  image_url: string;
  created_at: string;
}

export interface BannerResponse {
  message?: string;
  data: Banner;
}